/**
 * Data digitada à mão no formato brasileiro (DD/MM/AAAA).
 *
 * Existe porque o seletor nativo (`<input type="date">`) em aparelhos antigos
 * obriga a rolar ano por ano até chegar, por exemplo, em 1950. Aqui a pessoa
 * digita só os números e o sistema continua trabalhando com AAAA-MM-DD, o
 * mesmo formato de antes (banco, serviços e tipos não mudam).
 */

/** Ano mais antigo aceito numa data de nascimento (mesma regra dos formulários de visitantes). */
export const ANO_MINIMO_NASCIMENTO = 1900;

const MESES_EXTENSO = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

const PAD = (n: number) => String(n).padStart(2, "0");

/** Mantém só dígitos (até 8) e coloca as barras: "15031950" -> "15/03/1950". */
export function aplicarMascaraData(texto: string): string {
  const d = texto.replace(/\D/g, "").slice(0, 8);
  let saida = d.slice(0, 2);
  if (d.length > 2) saida += `/${d.slice(2, 4)}`;
  if (d.length > 4) saida += `/${d.slice(4)}`;
  return saida;
}

/** "1950-03-15" -> "15/03/1950". Vazio ou fora do formato -> "". */
export function isoParaTexto(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

export type ResultadoDataDigitada =
  { ok: true; iso: string; extenso: string } | { ok: false; mensagem: string };

interface OpcoesData {
  /** Maior data aceita, em AAAA-MM-DD (normalmente "hoje"). */
  max: string;
  /** Menor data aceita, em AAAA-MM-DD. Padrão: 1º de janeiro de 1900. */
  min?: string;
}

/** Confere o texto "DD/MM/AAAA" e devolve a data em AAAA-MM-DD ou a mensagem de erro. */
export function interpretarDataDigitada(
  texto: string,
  { max, min = `${ANO_MINIMO_NASCIMENTO}-01-01` }: OpcoesData,
): ResultadoDataDigitada {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto);
  if (!m) {
    return { ok: false, mensagem: "Digite dia, mês e ano com 8 números. Exemplo: 15/03/1950." };
  }
  const dia = Number(m[1]);
  const mes = Number(m[2]);
  const ano = Number(m[3]);
  if (mes < 1 || mes > 12) return { ok: false, mensagem: "O mês deve estar entre 01 e 12." };
  if (dia < 1 || dia > 31) return { ok: false, mensagem: "O dia deve estar entre 01 e 31." };
  const d = new Date(Date.UTC(ano, mes - 1, dia));
  if (d.getUTCFullYear() !== ano || d.getUTCMonth() !== mes - 1 || d.getUTCDate() !== dia) {
    return {
      ok: false,
      mensagem: `Em ${MESES_EXTENSO[mes - 1]} de ${ano} não existe o dia ${PAD(dia)}. Confira o dia e o mês.`,
    };
  }
  const iso = `${ano}-${PAD(mes)}-${PAD(dia)}`;
  if (iso > max) return { ok: false, mensagem: "A data de nascimento não pode estar no futuro." };
  if (iso < min) {
    return {
      ok: false,
      mensagem: `Confira o ano. O cadastro aceita datas a partir de ${min.slice(0, 4)}.`,
    };
  }
  return { ok: true, iso, extenso: `${dia} de ${MESES_EXTENSO[mes - 1]} de ${ano}` };
}
